import {useState} from "react";
import {FormProvider, useForm} from "react-hook-form";
import {useSelector} from "react-redux";
import {yupResolver} from "@hookform/resolvers/yup";
import {css} from "@panda/css";
import {boolean, number, object, string} from "yup";

import {Button} from "@pg-design/button-module";
import {Link} from "@pg-design/link-module";
import {Loader} from "@pg-design/loader-module";
import {InputField, PhoneFieldControlled} from "@pg-design/rhf-components-module";
import {Text} from "@pg-design/text-module";
import {IAppError} from "@pg-mono/request-state";

import {IRPStore} from "../../../app/rp_reducer";
import {IApplicationFormValues} from "../../../application/types/IApplicationFormValues";
import {ApplicationSource, ApplicationSourceSection} from "../../../application/utils/ApplicationSource";
import {validationMessages} from "../../../form/utils/validation_messages";
import {InquiryButtonTypeName} from "../../../offer/detail/constants/inquiry_button_type";
import {UserMktOptins} from "../../../user/components/UserMktOptins";
import {ILeadFormHandleSubmit} from "../../types/ILeadFormHandleSubmit";
import {ILeadFormValues} from "../../types/ILeadFormValues";
import {LeadButtonTerms} from "../LeadButtonTerms";

interface IProps {
    initialValues: ILeadFormValues;
    showForm: boolean;
    handleSubmit: ILeadFormHandleSubmit;
    sourceSection: ApplicationSourceSection;
    source: ApplicationSource;
    isSubmitting?: boolean;
    headerInfoText?: string;
    submitButtonText?: string;
    showRodoInfo?: boolean;
}

export const LeadForm = (props: IProps) => {
    const {initialValues, showForm, headerInfoText, submitButtonText, showRodoInfo, source, sourceSection, handleSubmit, isSubmitting} = props;
    const [formVisible, setFormVisible] = useState(showForm);
    const codes = useSelector((state: IRPStore) => state.variables?.data?.country_codes);

    const form = useForm<IApplicationFormValues>({
        defaultValues: initialValues,
        mode: "onBlur",
        resolver: yupResolver(formSchema)
    });

    const handlers = {
        changeUserData: () => {
            setFormVisible(true);
        },
        submitData: async (values: ILeadFormValues) => {
            handleSubmit(values, (error) => {
                //show form when logged user has blocked email
                if (isValidationError(error) && !showForm) {
                    handlers.changeUserData();
                }
                if (isValidationError(error)) {
                    const errorsToSet = "appError" in error && error.appError && "fieldErrors" in error.appError ? error.appError.fieldErrors : error;

                    if (errorsToSet) {
                        for (const fieldName of Object.keys(errorsToSet)) {
                            const errorMessage = (errorsToSet as Record<string, string | string[]>)[fieldName];
                            form.setError(fieldName as keyof IApplicationFormValues, {
                                message: Array.isArray(errorMessage) ? errorMessage.join(", ") : errorMessage
                            });
                        }
                    }
                }
            });
        }
    };

    return (
        <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(handlers.submitData)} className={formWrapper}>
                <div className={formContainer}>
                    {headerInfoText && (
                        <Text variant="info_txt_1" color="var(--colors-gray-800)" className={textBoxStyle}>
                            {headerInfoText}
                        </Text>
                    )}
                    {(!formVisible && !initialValues) || formVisible ? (
                        <div className={inputWrapperStyle}>
                            <InputField name="name" placeholder="Imię i nazwisko" />
                            <InputField name="email" placeholder="E-mail" />
                            <PhoneFieldControlled name="phone" codes={codes ?? [{value: 48, label: "PL"}]} placeholder="Numer telefonu" maxMenuHeight={200} />
                        </div>
                    ) : (
                        <div className={userInfoBox}>
                            <Text variant="mini_header">Twoje dane</Text>
                            <Text variant="body_copy_2">
                                Imię i nazwisko: <strong>{initialValues.name}</strong>
                                <br />
                                E-mail: <strong>{initialValues.email}</strong>
                                <br />
                                Telefon: <strong>{initialValues.phone?.number}</strong>
                            </Text>
                            <Link underline onClick={handlers.changeUserData}>
                                Zmień dane
                            </Link>
                        </div>
                    )}
                    <UserMktOptins showForm={showForm} initialValues={initialValues} className={optins} formId={`${source}_${sourceSection}_lead_modal`} />
                    {isSubmitting ? (
                        <Loader className={loaderStyle} />
                    ) : (
                        <Button variant="filled_primary" type="submit" fullWidth size="x-small" disabled={isSubmitting}>
                            {submitButtonText ?? InquiryButtonTypeName.OFFER}
                        </Button>
                    )}
                    <div className={leadTerms}>
                        <LeadButtonTerms buttonText={submitButtonText ?? InquiryButtonTypeName.OFFER} />
                    </div>
                    {showRodoInfo && (
                        <>
                            <br />
                            <Text variant="info_txt_3" className={rodoTextStyle} color="var(--colors-gray-800)">
                                Administratorem danych osobowych świadczonych w związku z realizacja umowy jest Property Group sp. z o.o. –{" "}
                                <Link className={linkStyle} size="x-small" href="https://rynekpierwotny.pl/regulamin-rodo/" target="_blank">
                                    zasady przetwarzania danych osobowych.
                                </Link>
                                <br />
                                <br />
                                {`Kliknięcie „${submitButtonText}” oznacza przekazanie danych kontaktowych do dewelopera"}`} -{" "}
                                <Link className={linkStyle} size="x-small" href="https://rynekpierwotny.pl/regulamin-rodo/" target="_blank">
                                    dowiedz się więcej o przetwarzaniu Twoich danych.
                                </Link>
                            </Text>
                        </>
                    )}
                </div>
            </form>
        </FormProvider>
    );
};

// TODO: Verify if this function can be replace with global util with the same name
function isValidationError(error: unknown): error is {appError: IAppError} | Record<string, string[]> {
    if (typeof error !== "object" || error === null) {
        return false;
    }

    // Check for wrapped appError structure
    if ("appError" in error && (error.appError as IAppError).status === 400) {
        return true;
    }

    // Check for direct validation error structure (field: string[])
    return Object.values(error).every((value) => Array.isArray(value) && value.every((item) => typeof item === "string"));
}

const formSchema = object().shape({
    name: string().required(),
    email: string().required().email(),
    phone: object().shape({code: number().required(), number: string().required()}),
    marketing_email: boolean().required().oneOf([true], validationMessages.required)
});

const inputWrapperStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1,
    mb: 2
});

const formWrapper = css({
    md: {
        display: "flex",
        flexDirection: "column"
    }
});

const optins = css({
    p: 0,
    py: 1,
    mb: 1
});

const leadTerms = css({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    pt: 1
});

const formContainer = css({
    p: 2,
    backgroundColor: "var(--colors-gray-300)"
});

const textBoxStyle = css({
    p: 1,
    backgroundColor: "var(--colors-background-100)",
    borderRadius: 2,
    mb: 2
});

const userInfoBox = css({
    p: 2,
    backgroundColor: "var(--colors-background-100)",
    borderRadius: 2,
    mb: 2,
    display: "flex",
    flexDirection: "column",
    gap: 1
});

const rodoTextStyle = css({
    textAlign: "left"
});

const linkStyle = css({
    color: "var(--colors-grey-800)",
    textDecoration: "underline",
    cursor: "pointer"
});

const loaderStyle = css({
    display: "flex",
    justifyContent: "center"
});
import {useState} from "react";
import {FormProvider, useForm} from "react-hook-form";
import {useSelector} from "react-redux";
import {yupResolver} from "@hookform/resolvers/yup";
import {css} from "@panda/css";
import {boolean, number, object, string} from "yup";

import {Button} from "@pg-design/button-module";
import {Link} from "@pg-design/link-module";
import {Loader} from "@pg-design/loader-module";
import {InputField, PhoneFieldControlled} from "@pg-design/rhf-components-module";
import {Text} from "@pg-design/text-module";
import {IAppError} from "@pg-mono/request-state";

import {IRPStore} from "../../../app/rp_reducer";
import {IApplicationFormValues} from "../../../application/types/IApplicationFormValues";
import {ApplicationSource, ApplicationSourceSection} from "../../../application/utils/ApplicationSource";
import {validationMessages} from "../../../form/utils/validation_messages";
import {InquiryButtonTypeName} from "../../../offer/detail/constants/inquiry_button_type";
import {UserMktOptins} from "../../../user/components/UserMktOptins";
import {ILeadFormHandleSubmit} from "../../types/ILeadFormHandleSubmit";
import {ILeadFormValues} from "../../types/ILeadFormValues";
import {LeadButtonTerms} from "../LeadButtonTerms";

interface IProps {
    initialValues: ILeadFormValues;
    showForm: boolean;
    handleSubmit: ILeadFormHandleSubmit;
    sourceSection: ApplicationSourceSection;
    source: ApplicationSource;
    isSubmitting?: boolean;
    headerInfoText?: string;
    submitButtonText?: string;
    showRodoInfo?: boolean;
}

export const LeadForm = (props: IProps) => {
    const {initialValues, showForm, headerInfoText, submitButtonText, showRodoInfo, source, sourceSection, handleSubmit, isSubmitting} = props;
    const [formVisible, setFormVisible] = useState(showForm);
    const codes = useSelector((state: IRPStore) => state.variables?.data?.country_codes);

    const form = useForm<IApplicationFormValues>({
        defaultValues: initialValues,
        mode: "onBlur",
        resolver: yupResolver(formSchema)
    });

    const handlers = {
        changeUserData: () => {
            setFormVisible(true);
        },
        submitData: async (values: ILeadFormValues) => {
            handleSubmit(values, (error) => {
                //show form when logged user has blocked email
                if (isValidationError(error) && !showForm) {
                    handlers.changeUserData();
                }
                if (isValidationError(error)) {
                    const errorsToSet = "appError" in error && error.appError && "fieldErrors" in error.appError ? error.appError.fieldErrors : error;

                    if (errorsToSet) {
                        for (const fieldName of Object.keys(errorsToSet)) {
                            const errorMessage = (errorsToSet as Record<string, string | string[]>)[fieldName];
                            form.setError(fieldName as keyof IApplicationFormValues, {
                                message: Array.isArray(errorMessage) ? errorMessage.join(", ") : errorMessage
                            });
                        }
                    }
                }
            });
        }
    };

    return (
        <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(handlers.submitData)} className={formWrapper}>
                <div className={formContainer}>
                    {headerInfoText && (
                        <Text variant="info_txt_1" color="var(--colors-gray-800)" className={textBoxStyle}>
                            {headerInfoText}
                        </Text>
                    )}
                    {(!formVisible && !initialValues) || formVisible ? (
                        <div className={inputWrapperStyle}>
                            <InputField name="name" placeholder="Imię i nazwisko" />
                            <InputField name="email" placeholder="E-mail" />
                            <PhoneFieldControlled name="phone" codes={codes ?? [{value: 48, label: "PL"}]} placeholder="Numer telefonu" maxMenuHeight={200} />
                        </div>
                    ) : (
                        <div className={userInfoBox}>
                            <Text variant="mini_header">Twoje dane</Text>
                            <Text variant="body_copy_2">
                                Imię i nazwisko: <strong>{initialValues.name}</strong>
                                <br />
                                E-mail: <strong>{initialValues.email}</strong>
                                <br />
                                Telefon: <strong>{initialValues.phone?.number}</strong>
                            </Text>
                            <Link underline onClick={handlers.changeUserData}>
                                Zmień dane
                            </Link>
                        </div>
                    )}
                    <UserMktOptins showForm={showForm} initialValues={initialValues} className={optins} formId={`${source}_${sourceSection}_lead_modal`} />
                    {isSubmitting ? (
                        <Loader className={loaderStyle} />
                    ) : (
                        <Button variant="filled_primary" type="submit" fullWidth size="x-small" disabled={isSubmitting}>
                            {submitButtonText ?? InquiryButtonTypeName.OFFER}
                        </Button>
                    )}
                    <div className={leadTerms}>
                        <LeadButtonTerms buttonText={submitButtonText ?? InquiryButtonTypeName.OFFER} />
                    </div>
                    {showRodoInfo && (
                        <>
                            <br />
                            <Text variant="info_txt_3" className={rodoTextStyle} color="var(--colors-gray-800)">
                                Administratorem danych osobowych świadczonych w związku z realizacja umowy jest Property Group sp. z o.o. –{" "}
                                <Link className={linkStyle} size="x-small" href="https://rynekpierwotny.pl/regulamin-rodo/" target="_blank">
                                    zasady przetwarzania danych osobowych.
                                </Link>
                                <br />
                                <br />
                                {`Kliknięcie „${submitButtonText}” oznacza przekazanie danych kontaktowych do dewelopera"}`} -{" "}
                                <Link className={linkStyle} size="x-small" href="https://rynekpierwotny.pl/regulamin-rodo/" target="_blank">
                                    dowiedz się więcej o przetwarzaniu Twoich danych.
                                </Link>
                            </Text>
                        </>
                    )}
                </div>
            </form>
        </FormProvider>
    );
};

// TODO: Verify if this function can be replace with global util with the same name
function isValidationError(error: unknown): error is {appError: IAppError} | Record<string, string[]> {
    if (typeof error !== "object" || error === null) {
        return false;
    }

    // Check for wrapped appError structure
    if ("appError" in error && (error.appError as IAppError).status === 400) {
        return true;
    }

    // Check for direct validation error structure (field: string[])
    return Object.values(error).every((value) => Array.isArray(value) && value.every((item) => typeof item === "string"));
}

const formSchema = object().shape({
    name: string().required(),
    email: string().required().email(),
    phone: object().shape({code: number().required(), number: string().required()}),
    marketing_email: boolean().required().oneOf([true], validationMessages.required)
});

const inputWrapperStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1,
    mb: 2
});

const formWrapper = css({
    md: {
        display: "flex",
        flexDirection: "column"
    }
});

const optins = css({
    p: 0,
    py: 1,
    mb: 1
});

const leadTerms = css({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    pt: 1
});

const formContainer = css({
    p: 2,
    backgroundColor: "var(--colors-gray-300)"
});

const textBoxStyle = css({
    p: 1,
    backgroundColor: "var(--colors-background-100)",
    borderRadius: 2,
    mb: 2
});

const userInfoBox = css({
    p: 2,
    backgroundColor: "var(--colors-background-100)",
    borderRadius: 2,
    mb: 2,
    display: "flex",
    flexDirection: "column",
    gap: 1
});

const rodoTextStyle = css({
    textAlign: "left"
});

const linkStyle = css({
    color: "var(--colors-grey-800)",
    textDecoration: "underline",
    cursor: "pointer"
});

const loaderStyle = css({
    display: "flex",
    justifyContent: "center"
});
